import React, { useState } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";

/**
 * Reusable password input with a show/hide eye toggle.
 *
 * Drop-in replacement for `<input type="password" />` — pass any standard input
 * props (value, onChange, placeholder, required, etc.). Lock icon on the left
 * is optional (`showLockIcon=false` for inline contexts like UsersPanel).
 *
 * Always sets aria-pressed on the toggle for screen readers, and forwards
 * data-testid prefixes so individual instances can be uniquely targeted.
 */
export const PasswordInput = React.forwardRef(function PasswordInput(
    { className = "", showLockIcon = true, testId, ...rest },
    ref
) {
    const [reveal, setReveal] = useState(false);
    const baseClass = "w-full rounded-xl border border-black/10 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all";
    const padClass = showLockIcon ? "pl-11 pr-12" : "pl-4 pr-12";
    return (
        <div className="relative">
            {showLockIcon && (
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
            )}
            <input
                {...rest}
                ref={ref}
                type={reveal ? "text" : "password"}
                data-testid={testId || rest["data-testid"]}
                className={`${baseClass} ${padClass} ${className}`}
            />
            <button
                type="button"
                onClick={() => setReveal((v) => !v)}
                aria-label={reveal ? "Hide password" : "Show password"}
                aria-pressed={reveal}
                data-testid={testId ? `${testId}-toggle` : "password-toggle"}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-[#4A5568] hover:text-[#1C252E] hover:bg-[#F4F1EA] transition-colors"
            >
                {reveal
                    ? <EyeOff className="w-4 h-4" strokeWidth={1.5} />
                    : <Eye className="w-4 h-4" strokeWidth={1.5} />}
            </button>
        </div>
    );
});

export default PasswordInput;
